import { useState } from 'react';
import type { ReactNode } from 'react';
import { noop } from 'lodash';

import { T } from '@app/components/translation';
import { CancelButton } from '@app/components/buttons';
import Tooltip from '@app/components/tooltip';
import Confirmable from '@app/components/confirmable';
import Button from '@app/components/button/button-fc';
import EmailIcon from '@app/components/icons/email';
import DownloadIcon from '@app/components/icons/download';

import style from './export-base.module.scss';

type Props = {
  tooltip: ReactNode;
  header: ReactNode;
  onOpen?: (setHasError: (hasError: boolean) => void) => void;
  onExport: (onSuccess: () => void, onFailure: (statusCode: number) => void) => void;
  renderBody: () => ReactNode;
  setErrorStatusCode: (statusCode: number | undefined) => void;
  setAgreements?: (agreements: null) => void;
  disabled: boolean;
};

export const ExportModalBase = ({
  tooltip,
  header,
  onExport,
  onOpen,
  renderBody,
  setErrorStatusCode,
  setAgreements,
  disabled,
}: Props) => {
  const [success, setSuccess] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleOpen = () => {
    if (onOpen) {
      onOpen(setHasError);
    }

    setSuccess(false);
  };

  const resetStates = () => {
    setSuccess(false);
    setHasError(false);
    setErrorStatusCode(undefined);
    setAgreements?.(null);
  };

  const handleExport = (onSuccess: () => void, onFailure: (statusCode: number) => void) => () => {
    onExport(onSuccess, onFailure);
  };

  const handleFailure = (statusCode: number) => {
    setErrorStatusCode(statusCode);
  };

  const handleSuccess = () => {
    setSuccess(true);
  };

  const getActions = ({ closeConfirmation }: { closeConfirmation: () => void }) => (
    <>
      <CancelButton onClick={closeConfirmation} />
      <Button
        customClass={style.RequestButton}
        data-testid="confirm"
        disabled={disabled}
        icon={EmailIcon}
        kind="primary"
        onClick={handleExport(handleSuccess, handleFailure)}
      >
        <T
          _str="Request export"
          _comment="Confirm button text in the export modal"
        />
      </Button>
    </>
  );

  return (
    <Confirmable
      header={header}
      success={success}
      body={renderBody()}
      actions={getActions}
      error={hasError}
      onEnter={noop}
      onOpen={handleOpen}
      onClose={resetStates}
    >
      {(onClick) => (
        <Tooltip
          messageClassName={style.TooltipText}
          message={tooltip}
          sideOffset={5}
          side="top"
        >
          <Button
            kind="ghost"
            onClick={onClick}
            customClass={style.ExportButton}
          >
            <span className={style.ExportButtonLabel}>
              <DownloadIcon height="12px" />
              <T
                _str="Export"
                _comment="Export button text"
              />
            </span>
          </Button>
        </Tooltip>
      )}
    </Confirmable>
  );
};
